import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  MessageSquare,
  BookOpen,
  Bell,
  PlayCircle,
  Search,
  GraduationCap,
} from "./icons";
import { useAuth } from "../contexts/AuthContext";
import { usersAPI } from "../services/api";
import { useSiteConfig } from "../hooks/useSiteConfig";
import "./Navbar.css";

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { marketplaceEnabled } = useSiteConfig();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [demoActivityCount, setDemoActivityCount] = useState(0);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showProfileMenu]);

  // Fetch inbox unread count for all authenticated users
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const res = await usersAPI.getInboxUnreadCount();
        setUnreadNotificationCount(res.data?.unread ?? 0);
      } catch {
        setUnreadNotificationCount(0);
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    const onRefresh = () => fetchUnread();
    window.addEventListener("inbox-refresh", onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("inbox-refresh", onRefresh);
    };
  }, [user]);

  // Fetch demo activity count for manager (demos with activity in last 24h)
  useEffect(() => {
    if (user?.user_type !== "manager") return;
    const fetchCount = async () => {
      try {
        const res = await usersAPI.getDemoActivityCount();
        setDemoActivityCount(res.data?.count ?? 0);
      } catch {
        setDemoActivityCount(0);
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    const onRefresh = () => fetchCount();
    window.addEventListener("demo-activity-refresh", onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("demo-activity-refresh", onRefresh);
    };
  }, [user?.user_type, user?.id]);

  const handleLogout = async () => {
    setShowProfileMenu(false);
    await logout();
    navigate("/");
  };

  const handleStudentProfile = () => {
    setShowProfileMenu(false);
    navigate("/dashboard", { state: { openStudentProfile: true } });
  };

  const handleStudentOpportunities = () => {
    setShowProfileMenu(false);
    navigate("/student-opportunities");
  };

  const handleProfileClick = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleStudentManagement = () => {
    setShowProfileMenu(false);
    navigate("/student-management");
  };

  const handleParentProfile = () => {
    setShowProfileMenu(false);
    navigate("/parent-profile");
  };

  const handleParentOpportunities = () => {
    setShowProfileMenu(false);
    navigate("/parent-opportunities");
  };

  const handleTutorProfile = () => {
    setShowProfileMenu(false);
    if (user?.user_type === "mentor") {
      navigate("/mentor-portal?tab=profile");
      return;
    }
    navigate("/tutor-portal", { state: { openTutorProfile: true } });
  };

  const handleTutorMentorOpportunities = () => {
    setShowProfileMenu(false);
    navigate("/tutor-mentor-opportunities");
  };

  const navigateToTutorTab = (tab: string) => {
    setShowProfileMenu(false);
    const basePath =
      user?.user_type === "mentor" ? "/mentor-portal" : "/tutor-portal";
    navigate(`${basePath}?tab=${tab}`);
  };

  const handleManagerProfile = () => {
    setShowProfileMenu(false);
    navigate("/manager-portal?tab=profile");
  };

  const navigateToManagerTab = (tab: string) => {
    setShowProfileMenu(false);
    navigate(`/manager-portal?tab=${tab}`);
  };

  return (
    <nav className="navbar">
      <div className="navbar-content">
        <Link to="/" className="navbar-brand">
          <img
            src="/yfp_logo.png"
            alt="YourFuturePrep"
            className="navbar-logo"
          />
          <span className="navbar-brand-text">
            Your Kid's Future Preparation Starts Here...
          </span>
        </Link>

        <div className="navbar-links">
          <Link
            to="/opportunities"
            className="nav-link"
            data-testid="nav-opportunities-link"
          >
            <Search size={18} strokeWidth={2} className="nav-link-icon" />
            Opportunities
          </Link>
          <Link
            to="/courses"
            className="nav-link"
            data-testid="nav-courses-link"
          >
            <BookOpen size={18} strokeWidth={2} className="nav-link-icon" />
            Courses
          </Link>
          {marketplaceEnabled && (
            <Link
              to="/classes/group"
              className="nav-link"
              data-testid="nav-classes-link"
            >
              <GraduationCap
                size={18}
                strokeWidth={2}
                className="nav-link-icon"
              />
              Classes
            </Link>
          )}
          <Link to="/videos" className="nav-link" data-testid="nav-videos-link">
            <PlayCircle size={18} strokeWidth={2} className="nav-link-icon" />
            Videos
          </Link>
          <Link to="/blogs" className="nav-link">
            <FileText size={18} strokeWidth={2} className="nav-link-icon" />
            Blogs
          </Link>
          <Link to="/forums" className="nav-link">
            <MessageSquare
              size={18}
              strokeWidth={2}
              className="nav-link-icon"
            />
            Forums
          </Link>
          {user ? (
            <>
              <Link to="/dashboard" className="nav-link">
                <LayoutDashboard
                  size={18}
                  strokeWidth={2}
                  className="nav-link-icon"
                />
                Dashboard
              </Link>
              <Link
                to="/inbox"
                className="nav-link nav-bell-link"
                data-testid="nav-inbox-bell"
                aria-label={
                  unreadNotificationCount > 0
                    ? `${unreadNotificationCount} unread messages`
                    : "Inbox"
                }
              >
                <Bell size={20} strokeWidth={2} className="nav-link-icon" />
                {unreadNotificationCount > 0 && (
                  <span className="nav-bell-badge" aria-hidden="true">
                    {unreadNotificationCount > 99
                      ? "99+"
                      : unreadNotificationCount}
                  </span>
                )}
              </Link>
              <div className="profile-menu" ref={profileMenuRef}>
                <button
                  className="profile-button"
                  onClick={handleProfileClick}
                  aria-label="Open profile menu"
                  aria-expanded={showProfileMenu}
                >
                  <span className="user-info">
                    {user.username} ({user.user_type})
                    {[
                      "parent",
                      "student",
                      "tutor",
                      "mentor",
                      "manager",
                    ].includes(user.user_type) && (
                      <span
                        className="opportunity-points-badge"
                        title="Approved opportunity submissions"
                      >
                        {" "}
                        ★ {user.opportunity_points ?? 0}
                      </span>
                    )}
                  </span>
                  <span className="dropdown-arrow">▼</span>
                </button>
                {showProfileMenu && (
                  <div className="profile-dropdown">
                    {user.user_type === "parent" && (
                      <>
                        <button
                          onClick={handleParentProfile}
                          className="dropdown-item"
                        >
                          My Profile
                        </button>
                        <button
                          onClick={handleParentOpportunities}
                          className="dropdown-item"
                        >
                          Opportunities
                        </button>
                        <button
                          onClick={handleStudentManagement}
                          className="dropdown-item"
                        >
                          Manage Students
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/feature-request");
                          }}
                          className="dropdown-item"
                        >
                          Feature Request
                        </button>
                        <hr className="dropdown-divider" />
                      </>
                    )}
                    {user.user_type === "tutor" && (
                      <>
                        <button
                          onClick={handleTutorProfile}
                          className="dropdown-item"
                        >
                          My Profile
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("challenges")}
                        >
                          Topics
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("submissions")}
                        >
                          Submissions
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("attendance")}
                        >
                          Attendance
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("schedule")}
                        >
                          Schedule
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("ads")}
                        >
                          Advertisements
                        </button>
                        <button
                          onClick={handleTutorMentorOpportunities}
                          className="dropdown-item"
                        >
                          Opportunities
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/feature-request");
                          }}
                          className="dropdown-item"
                        >
                          Feature Request
                        </button>
                        <hr className="dropdown-divider" />
                      </>
                    )}
                    {user.user_type === "mentor" && (
                      <>
                        <button
                          onClick={handleTutorProfile}
                          className="dropdown-item"
                          data-testid="nav-mentor-profile-btn"
                        >
                          My Profile
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("schedule")}
                          data-testid="nav-mentor-schedule-btn"
                        >
                          Schedule
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("challenges")}
                          data-testid="nav-mentor-challenges-btn"
                        >
                          Topics
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("submissions")}
                          data-testid="nav-mentor-submissions-btn"
                        >
                          Submissions
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("documents")}
                          data-testid="nav-mentor-documents-btn"
                        >
                          Pending Docs
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("attendance")}
                          data-testid="nav-mentor-attendance-btn"
                        >
                          Attendance
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToTutorTab("ads")}
                          data-testid="nav-mentor-ads-btn"
                        >
                          Advertisements
                        </button>
                        <button
                          onClick={handleTutorMentorOpportunities}
                          className="dropdown-item"
                        >
                          Opportunities
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/feature-request");
                          }}
                          className="dropdown-item"
                        >
                          Feature Request
                        </button>
                        <hr className="dropdown-divider" />
                      </>
                    )}
                    {user.user_type === "manager" && (
                      <>
                        <button
                          onClick={handleManagerProfile}
                          className="dropdown-item"
                        >
                          My Profile
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToManagerTab("schedule")}
                        >
                          Schedules
                        </button>
                        <button
                          className={`dropdown-item ${demoActivityCount > 0 ? "dropdown-item-with-badge" : ""}`}
                          onClick={() => navigateToManagerTab("demos")}
                        >
                          Demos
                          {demoActivityCount > 0 && (
                            <span
                              className="notification-badge"
                              aria-label={`${demoActivityCount} demo updates`}
                            >
                              {demoActivityCount > 99
                                ? "99+"
                                : demoActivityCount}
                            </span>
                          )}
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToManagerTab("notifications")}
                        >
                          Notifications
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToManagerTab("dues")}
                        >
                          Dues
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => navigateToManagerTab("opportunities")}
                        >
                          Opportunities
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() =>
                            navigateToManagerTab("parent-interests")
                          }
                        >
                          Messages
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/feature-request");
                          }}
                          className="dropdown-item"
                        >
                          Feature Request
                        </button>
                        <hr className="dropdown-divider" />
                      </>
                    )}
                    {user.user_type === "student" && (
                      <>
                        <button
                          onClick={handleStudentProfile}
                          className="dropdown-item"
                        >
                          My Profile
                        </button>
                        <button
                          onClick={handleStudentOpportunities}
                          className="dropdown-item"
                        >
                          Opportunities
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/feature-request");
                          }}
                          className="dropdown-item"
                        >
                          Feature Request
                        </button>
                        <hr className="dropdown-divider" />
                      </>
                    )}
                    {user.user_type === "intern" && (
                      <>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/intern-profile");
                          }}
                          className="dropdown-item"
                          data-testid="intern-nav-profile"
                          aria-label="My Profile"
                        >
                          My Profile
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/intern-dashboard?section=topics");
                          }}
                          className="dropdown-item"
                          data-testid="intern-nav-topics"
                          aria-label="Topics"
                        >
                          Topics
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/intern-dashboard?section=schedule");
                          }}
                          className="dropdown-item"
                          data-testid="intern-nav-schedule"
                          aria-label="Schedule"
                        >
                          Schedule
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/intern-opportunities");
                          }}
                          className="dropdown-item"
                          data-testid="intern-nav-opportunities"
                          aria-label="Opportunities"
                        >
                          Opportunities
                        </button>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/feature-request");
                          }}
                          className="dropdown-item"
                          data-testid="intern-nav-feature-request"
                          aria-label="Feature Request"
                        >
                          Feature Request
                        </button>
                        <hr className="dropdown-divider" />
                      </>
                    )}
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate("/inbox");
                      }}
                      className={`dropdown-item ${unreadNotificationCount > 0 ? "dropdown-item-with-badge" : ""}`}
                      data-testid={
                        user.user_type === "intern"
                          ? "intern-nav-inbox"
                          : "inbox-dropdown-btn"
                      }
                      aria-label="Inbox"
                    >
                      Inbox
                      {unreadNotificationCount > 0 && (
                        <span
                          className="notification-badge"
                          aria-label={`${unreadNotificationCount} unread messages`}
                        >
                          {unreadNotificationCount > 99
                            ? "99+"
                            : unreadNotificationCount}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate("/change-password");
                      }}
                      className="dropdown-item"
                      data-testid={
                        user.user_type === "intern"
                          ? "intern-nav-change-password"
                          : undefined
                      }
                      aria-label="Change Password"
                    >
                      Change Password
                    </button>
                    <hr className="dropdown-divider" />
                    <button
                      onClick={handleLogout}
                      className="dropdown-item logout-item"
                      data-testid={
                        user.user_type === "intern"
                          ? "intern-nav-logout"
                          : undefined
                      }
                      aria-label="Logout"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link">
                Login
              </Link>
              <Link to="/register" className="nav-link">
                Register
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
